import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'Components/Atoms/Button/Button';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFCategorySelect, RHFSelectContact } from 'Components/Organisms/HookForm';
import clsx from 'clsx';
import classes from './ForecastValueCreate.module.css';
import DurationSelector from './DurationSelector';
import Select from 'Components/Atoms/Select/Select';
import Input from 'Components/Atoms/Input/Input';
import { getCurrencySymbol } from 'Utils/formatters';
import Divider from 'Components/Atoms/Divider/Divider';
import { useTranslation } from 'react-i18next';
import useRepeatsByPeriodicity from './useRepeatsByPeriodicity';
import useOperationSpans from './useOperationSpans';
import useCurrency from 'Hooks/useCurrency';
import useLocale from 'Hooks/useLocale';

const getVariation = (variationId, value) => {
    const numberValue = Math.abs(Number(value));

    switch (variationId) {
        case 'increase_by':
            return {
                variation: 'multiply',
                variation_value: (numberValue + 100) / 100,
            };
        case 'decrease_by':
            return {
                variation: 'multiply',
                variation_value: Math.max((100 - numberValue) / 100, 0),
            };
        case 'add_value':
            return {
                variation: 'sum',
                variation_value: numberValue,
            };
        case 'substract_value':
            return {
                variation: 'sum',
                variation_value: -numberValue,
            };
        case 'multiply_by':
            return {
                variation: 'multiply',
                variation_value: numberValue,
            };
        default:
            return {};
    }
};

export default function ForecastValueCreateCalculatedForm(props) {
    const {
        processing,
        onCancel,
        onSubmit,
        className,
        periodicity,
        categoryType,
        periodKey,
        predictedOn,
        referenceId = '',
        defaultValues,
        defaultVariation,
        defaultOperationSpan,
    } = props;
    const { repeat_for, repeat_every } = useRepeatsByPeriodicity({ periodicity, periodKey });

    const { t } = useTranslation('forecasts');
    const currency = useCurrency();
    const locale = useLocale();

    const transactionTypeOptions = [
        {
            id: 'income_category',
            label: t('cell.income-category'),
        },
        {
            id: 'expense_category',
            label: t('cell.expense-category'),
        },
    ];

    const contactTypeOptions = [
        {
            id: 'income_category',
            label: t('cell.income-contact'),
        },
        {
            id: 'expense_category',
            label: t('cell.expense-contact'),
        },
    ];

    const operationOptions = [
        {
            id: 'pointer',
            label: t('cell.operation.pointer'),
        },
        {
            id: 'avg',
            label: t('cell.operation.avg'),
        },
        {
            id: 'max',
            label: t('cell.operation.max'),
        },
        {
            id: 'min',
            label: t('cell.operation.min'),
        },
    ];

    const variationOptions = [
        {
            id: 'increase_by',
            label: t('cell.operation.increase-by'),
        },
        {
            id: 'decrease_by',
            label: t('cell.operation.decrease-by'),
        },
        {
            id: 'add_value',
            label: t('cell.operation.add-value'),
        },
        {
            id: 'substract_value',
            label: t('cell.operation.substract-value'),
        },
        {
            id: 'multiply_by',
            label: t('cell.operation.multiply-by'),
        },
    ];

    const [operationValue, setOperationValue] = useState(defaultOperationSpan ?? 0);
    const [variationValue, setVariationValue] = useState(defaultVariation?.variationValue ?? 0);
    const [variationId, setVariationId] = useState(defaultVariation?.variationId ?? 'increase_by');

    const methods = useForm({
        defaultValues: {
            reference: categoryType === 'incomes' ? 'income_category' : 'expense_category',
            reference_id: referenceId,
            operation: 'pointer',
            repeat_for: repeat_for[0].id,
            repeat_every: repeat_every[0].id,
            ...defaultValues,
        },
    });

    const { handleSubmit, watch } = methods;

    const operation = watch('operation');
    const hasRepetition = watch('repeat_for') > 1;

    const onSubmitInternal = (data) => {
        const variationData = getVariation(variationId, variationValue);

        onSubmit({
            ...data,
            ...variationData,
            operation_span: operationValue ?? 0,
        });
    };
    const operationValueOptions = useOperationSpans({ periodicity, operation });
    const variationValueSuffix = useMemo(() => {
        if (variationId === 'increase_by' || variationId === 'decrease_by') {
            return '%';
        }

        if (variationId === 'add_value' || variationId === 'substract_value') {
            return getCurrencySymbol(locale, currency);
        }

        return '';
    }, [variationId, currency, locale]);

    useEffect(() => {
        const isValid = operationValueOptions.some((option) => option.id === operationValue);

        if (!isValid) {
            setOperationValue(operationValueOptions[0].id);
        }
    }, [operationValueOptions]);

    return (
        <FormProvider
            className={clsx('flex flex-col grow', className)}
            methods={methods}
            onSubmit={handleSubmit(onSubmitInternal)}
        >
            <div className="grid gap-6 pb-1">
                <div className={classes.card}>
                    <div className={classes.label}>{t('cell.use-as-base')}</div>
                    <div className="grid gap-4">
                        {predictedOn === 'taxonomy_tag' && (
                            <>
                                <RHFSelect className="w-full" name="reference" options={transactionTypeOptions} />
                                <RHFCategorySelect name="reference_id" />
                            </>
                        )}
                        {predictedOn === 'contact' && (
                            <>
                                <RHFSelect className="w-full" name="reference" options={contactTypeOptions} />
                                <RHFSelectContact name="reference_id" />
                            </>
                        )}
                    </div>
                </div>

                <div className={classes.card}>
                    <div className={classes.label}>{t('cell.calculate')}</div>
                    <div className="grid gap-4">
                        <RHFSelect className="w-full" name="operation" options={operationOptions} />
                        <DurationSelector
                            value={operationValue}
                            options={operationValueOptions}
                            onChange={setOperationValue}
                            periodicity={periodicity}
                        />
                    </div>
                </div>

                <div className={classes.card}>
                    <div className={classes.label}>{t('cell.and')}</div>
                    <div className="flex gap-2">
                        <Select
                            className="w-full"
                            options={variationOptions.map((option) => ({ ...option, value: option.id }))}
                            value={variationId}
                            onChange={(value) => setVariationId(value)}
                        />
                        <div style={{ width: 140 }}>
                            <Input
                                min={0}
                                step={0.01}
                                type="number"
                                value={variationValue}
                                onChange={(ev) => setVariationValue(ev.target.value)}
                                suffix={variationValueSuffix}
                            />
                        </div>
                    </div>
                </div>

                <Divider className="my-1" />

                <div className={classes.card}>
                    <div className="flex justify-content-evenly gap-2">
                        <div className="grow">
                            <div className={classes.label}>{t('cell.repetition')}</div>
                            <RHFSelect className="w-full" name="repeat_for" options={repeat_for} />
                        </div>
                        <div className="grow">
                            <div className={classes.label}>{t('cell.every')}</div>
                            <RHFSelect
                                className="w-full"
                                name="repeat_every"
                                options={repeat_every}
                                disabled={!hasRepetition}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grow" />

            <div className="sticky bottom-0 bg-white py-6">
                <div className="flex gap-2">
                    <Button variant="primary" type="submit" className="grow" isBusy={processing}>
                        {t('cell.apply')}
                    </Button>
                    <Button className="grow" onClick={onCancel}>
                        {t('cell.cancel')}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

ForecastValueCreateCalculatedForm.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    categoryType: PropTypes.string,
    processing: PropTypes.bool,
    className: PropTypes.string,
    periodicity: PropTypes.string,
    periodKey: PropTypes.string,
    predictedOn: PropTypes.string,
    referenceId: PropTypes.string,
    defaultValues: PropTypes.object,
    defaultVariation: PropTypes.shape({
        variationId: PropTypes.string,
        variationValue: PropTypes.number,
    }),
    defaultOperationSpan: PropTypes.number,
};
